import React from 'react'
import { Route, Switch } from 'react-router-dom'

// @ts-expect-error: no types in this @gnomad/ui version
import { ExternalLink } from '@gnomad/ui'

import { AGBDataDescription } from '../agb/AGBHomePage'
import datasetConfig from '../datasetConfig'
import Fetch from './Fetch'
import { DatasetId } from './Browser'
import getDatasetBrowserUrl from './datasetBrowserUrl'

const BrowserLink = ({ datasetId }: { datasetId: DatasetId }) => {
  return (
    <Switch>
      <Route
        path="/results"
        render={() => (
          <ExternalLink href={getDatasetBrowserUrl(datasetId, '/results')}>
            View results in {datasetId} browser
          </ExternalLink>
        )}
      />
      <Route
        path="/gene/:geneIdOrName"
        render={({ location, match }) => (
          <Fetch path={`/gene/${match.params.geneIdOrName}`}>
            {({ data }) => {
              const label = data && data.gene ? data.gene.symbol : 'this gene'
              return (
                <ExternalLink href={getDatasetBrowserUrl(datasetId, location.pathname)}>
                  View {label} in {datasetId} browser
                </ExternalLink>
              )
            }}
          </Fetch>
        )}
      />
      <Route
        render={() => (
          <ExternalLink href={getDatasetBrowserUrl(datasetId)}>
            Open {datasetId} browser
          </ExternalLink>
        )}
      />
    </Switch>
  )
}

type DatasetLink = {
  id: DatasetId
  phenotype: string
  description: string | React.ReactNode
}

const datasetLinks: DatasetLink[] = [
  {
    id: 'AGB',
    phenotype: 'Autism',
    description: (
      <AGBDataDescription
        aboutLink={<ExternalLink href={getDatasetBrowserUrl('AGB', '/about')}>here</ExternalLink>}
        termsLink={
          <ExternalLink href={getDatasetBrowserUrl('AGB', '/terms')}>terms of use</ExternalLink>
        }
      />
    ),
  },
  {
    id: 'BipEx',
    phenotype: 'Bipolar disorder',
    description:
      'The Bipolar Exome (BipEx) sequencing project is a collaboration between multiple institutions across the globe, which aims to increase our understanding of the disease architecture of bipolar disorder.',
  },
  {
    id: 'Epi25',
    phenotype: 'Epilepsy',
    description:
      'The Epi25 collaborative is a global collaboration committed to aggregating, sequencing, and deep-phenotyping up to 25,000 epilepsy patients to advance epilepsy genetics research. The Epi25 whole-exome sequencing (WES) case-control study is one of the collaborative&apos;s ongoing endeavors that aims to characterize the contribution of rare genetic variation to a spectrum of epilepsy syndromes to identify individual risk genes.',
  },
  {
    id: 'GP2',
    phenotype: 'Parkinsons',
    description: (
      <p>
        The Global Parkinson’s Genetics Program (
        <ExternalLink href="https://gp2.org/">GP2</ExternalLink>) is a resource program of the
        Aligning Science Across Parkinson&apos;s (
        <ExternalLink href="https://parkinsonsroadmap.org/#">ASAP</ExternalLink>) initiative, which
        is managed by the Coalition for Aligning Science and implemented by the Michael J. Fox
        Foundation. GP2 serves as a central hub to generate genetic data from studies across the
        globe and harmonize corresponding clinical data. Our goal is to genetically characterize
        over 250,000 volunteers around the world to further understand the genetic architecture of
        Parkinson’s disease (PD).
      </p>
    ),
  },
  {
    id: 'IBD',
    phenotype: 'Irritable Bowel Disease',
    description:
      'The Inflammatory Bowel Disease (IBD) Sequencing Consortium is a global collaboration dedicated to aggregating, generating, and analyzing high-throughput sequencing data of inflammatory bowel disease patients to improve our understanding of disease architecture and advance gene discovery. The consortium was formed in 2014 with a commitment to data sharing, diversity, and inclusivity - we hope that the findings from this study and others like it will provide a foundation for further investigation of disease mechanisms and therapeutic discovery. This browser is part of that overall effort to display and share these results with the wider scientific community. Partnering with the Broad Institute, the IBD Sequencing Consortium has sequenced more than 50,000 patients as of 2024 from 50 research cohorts across the world.',
  },
  {
    id: 'SCHEMA',
    phenotype: 'Schizophrenia',
    description:
      'The Schizophrenia Exome Sequencing Meta-analysis (SCHEMA) consortium is a large multi-site collaboration dedicated to aggregating, generating, and analyzing high-throughput sequencing data of schizophrenia patients to improve our understanding of disease architecture and advance gene discovery. The first results of this study have provided genome-wide significant results associating rare variants in individual genes to risk of schizophrenia, and later releases are planned with larger number of samples that will further increase power.',
  },
]

const OtherStudies = () => (
  <>
    <p>
      This website contains results from one of several case-control studies of psychiatric diseases
      done at the Broad Institute. Results of other studies are available at the following websites:
    </p>

    {datasetLinks
      .filter((ds) => ds.id !== datasetConfig.datasetId)
      .map((ds) => {
        return (
          <>
            <h2>
              {ds.phenotype} -{' '}
              <ExternalLink href={getDatasetBrowserUrl(ds.id)}>{ds.id}</ExternalLink>
            </h2>
            <p>{ds.description}</p>
            <p>
              <BrowserLink datasetId={ds.id} />
            </p>
          </>
        )
      })}
  </>
)

export default OtherStudies
